import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import toastr from "../../utils/toast";
import WhoWeKnow from "../../components/Modals/workstation/WhoWeKnow";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// The Search Map's colours.
const GREEN = "hsl(142 71% 45%";
const RED = "hsl(0 84% 60%";
// The prototype's primary, which its AI suggestions are tinted with.
const ORANGE = "hsl(22 90% 55%";
// The Unclassified card, set apart from the industries and the AI's orange.
const AMBER = { text: "#B45309", tint: "#FFFBEB", line: "#FCD34D" };

// How often the page asks whether a run has finished.
const POLL_MS = 3000;

// The map's bucket for companies PINE has no industry for. Not an industry,
// so the AI never suggests for it.
const UNCLASSIFIED = "unclassified";
const sameIndustry = (a, b) =>
  String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
const isUnclassified = (industry) => sameIndustry(industry, UNCLASSIFIED);

const LABEL_STYLE = { fontSize: 11 };

const when = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/** Does a run cover this industry: the whole map, or this one card? */
const covers = (run, industry) =>
  !!run && (run.scope === "all" || sameIndustry(run.industry, industry));

/**
 * Gap Analysis - how well one role's Search Map is covered, overall and per
 * industry, and which companies each industry is missing.
 *
 * Coverage is read from the same map the grid shows, so the two always agree:
 * mapped (green) = a candidate on this role works there, no-poach (red), not
 * yet mapped (white) = neither.
 *
 * The suggestions come from a model, are saved, and are read back on every
 * later open. The first open of a role starts a run by itself; after that only
 * the refresh buttons do. A run takes up to a minute in the background - the
 * page asks every few seconds until it is done.
 */
export default function GapAnalysis() {
  const { id } = useParams();
  const { token } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const gridUrl = `/workstation/search_map/${id}`;
  // Always the role's workstation page, as on the Search Map (see there).
  const handleBack = () => navigate(`/workstation/view/${id}`);

  // Opened from the grid: going back is the way to the grid, so its own Back
  // still leads to the role page. Opened any other way: replace this page
  // with the grid, so the grid's Back does not return here.
  const fromGrid = location.state?.returnTo === gridUrl;
  const openGrid = () => {
    if (fromGrid && window.history.state?.idx > 0) navigate(-1);
    else navigate(gridUrl, { replace: true });
  };

  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // The saved suggestions and the state of the runs; null until loaded.
  const [ai, setAi] = useState(null);
  const [aiError, setAiError] = useState("");
  const [starting, setStarting] = useState(false);
  const [adding, setAdding] = useState(new Set());
  // "Who do we already know there?": null, or { name, mapCompanyId? }.
  const [whoWeKnow, setWhoWeKnow] = useState(null);
  // The first open starts a run by itself - once per visit to the page.
  const autoStarted = useRef(false);

  const loadMap = useCallback(async () => {
    try {
      const res = await axios.get(
        `${REACT_APP_API_BASE_URL}/workstation/search_map/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMap(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Could not load the search map.");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const loadAi = useCallback(async () => {
    try {
      const res = await axios.get(
        `${REACT_APP_API_BASE_URL}/workstation/gap_analysis/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAi(res.data);
      setAiError("");
      return res.data;
    } catch (err) {
      setAiError(
        err.response?.data?.error || "Could not load the AI suggestions.",
      );
      return null;
    }
  }, [id, token]);

  useEffect(() => {
    setLoading(true);
    autoStarted.current = false;
    loadMap();
    loadAi();
  }, [loadMap, loadAi]);

  const startRun = useCallback(
    // No argument: the whole page (companies and new industries). An industry:
    // that card only. newIndustries: the new-industries section only.
    async (industry = null, newIndustries = false) => {
      setStarting(true);
      try {
        await axios.post(
          `${REACT_APP_API_BASE_URL}/workstation/gap_analysis/${id}/run`,
          industry ? { industry } : newIndustries ? { new_industries: true } : {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        await loadAi();
      } catch (err) {
        toastr.error(
          err.response?.data?.error || "Could not start the AI suggestions.",
        );
      } finally {
        setStarting(false);
      }
    },
    [id, token, loadAi],
  );

  const columns = map?.columns || [];
  const hasIndustry = columns.some((c) => !isUnclassified(c.industry));
  const startingIndustry = ai?.starting_industry || null;
  const canSuggest = hasIndustry || !!startingIndustry;
  const running = ai?.running || null;
  const busy = starting || !!running;

  // First open: nothing has ever succeeded and nothing is running.
  useEffect(() => {
    if (!map || !ai || autoStarted.current) return;
    autoStarted.current = true;
    if (!ai.last_success && !ai.running && canSuggest) startRun();
  }, [map, ai, canSuggest, startRun]);

  // While a run goes, ask every few seconds whether it has finished.
  const runningId = running?.id;
  useEffect(() => {
    if (!runningId) return undefined;
    const timer = setInterval(loadAi, POLL_MS);
    return () => clearInterval(timer);
  }, [runningId, loadAi]);

  // Classifying the "Unclassified" companies: each one's LinkedIn page, then
  // PINE's industry, written into PINE. Starts by itself, as on the Search Map
  // (a run already going there is the same run, not a second one).
  const classifyUrl = `${REACT_APP_API_BASE_URL}/workstation/gap_analysis/${id}/classify`;
  const [classify, setClassify] = useState(null);
  const [undoing, setUndoing] = useState(new Set());
  const classifyStarted = useRef(false);
  const hasUnclassified = columns.some(
    (c) => isUnclassified(c.industry) && c.companies.length > 0,
  );

  const loadClassify = useCallback(async () => {
    try {
      const res = await axios.get(classifyUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClassify(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [classifyUrl, token]);

  const startClassify = useCallback(
    async (force = false) => {
      try {
        await axios.post(
          classifyUrl,
          { force },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        if (force) {
          toastr.error(err.response?.data?.error || "Could not start classifying.");
        }
      }
      await loadClassify();
    },
    [classifyUrl, token, loadClassify],
  );

  useEffect(() => {
    if (!map || classifyStarted.current) return;
    classifyStarted.current = true;
    if (hasUnclassified) startClassify(false);
    else loadClassify();
  }, [map, hasUnclassified, startClassify, loadClassify]);

  const classifyRunning = classify?.run?.status === "running";
  useEffect(() => {
    if (!classifyRunning) return undefined;
    const timer = setInterval(async () => {
      const state = await loadClassify();
      if (state && state.run?.status !== "running") loadMap();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [classifyRunning, loadClassify, loadMap]);

  const undoClassify = async (check) => {
    setUndoing((prev) => new Set(prev).add(check.id));
    try {
      await axios.post(
        `${classifyUrl}/${check.id}/undo`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastr.success(`${check.name} is unclassified again.`);
      await Promise.all([loadClassify(), loadMap()]);
    } catch (err) {
      toastr.error(err.response?.data?.error || "Could not undo that.");
    } finally {
      setUndoing((prev) => {
        const next = new Set(prev);
        next.delete(check.id);
        return next;
      });
    }
  };

  /**
   * The Unclassified card: what is still left and could not be placed, then
   * what was placed in PINE, each with Undo.
   */
  const renderUnclassifiedCard = (column) => {
    // Latest result per company: a company tried twice shows its newest.
    const latest = [];
    const seen = new Set();
    for (const k of classify?.checks || []) {
      const key = k.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      latest.push(k);
    }
    const done = latest.filter((k) => k.outcome === "classified" && !k.undone);
    // Only the ones still in the column - one removed from the map is gone.
    const inColumn = new Set(column.companies.map((c) => c.name.toLowerCase()));
    const notPlaced = latest.filter(
      (k) =>
        (k.outcome === "not_found" || k.outcome === "no_fit") &&
        inColumn.has(k.name.toLowerCase()),
    );
    const failed = classify?.run?.status === "failed";

    return (
      <div key={column.id} className="col-md-6 col-lg-4">
        <div
          className="rounded d-flex flex-column"
          style={{ height: 288, border: `1px solid ${AMBER.line}` }}
        >
          <div className="px-3 py-2 border-bottom">
            <div className="d-flex align-items-center justify-content-between gap-2">
              <h6 className="fw-semibold mb-0">{column.industry}</h6>
              <span
                className="d-inline-flex align-items-center gap-1 rounded-pill px-2 fw-semibold text-nowrap"
                style={{
                  ...LABEL_STYLE,
                  background: AMBER.tint,
                  color: AMBER.text,
                  border: `1px solid ${AMBER.line}`,
                }}
              >
                <i className="fa-solid fa-wand-magic-sparkles"></i>
                Auto-placing from LinkedIn
              </span>
            </div>
            <div className="d-flex gap-3 mt-2 small">
              <div>
                <span className="d-block fw-bold" style={{ fontSize: 16, lineHeight: 1.1, color: "#15803D" }}>
                  {done.length}
                </span>
                placed
              </div>
              <div>
                <span className="d-block fw-bold" style={{ fontSize: 16, lineHeight: 1.1, color: AMBER.text }}>
                  {column.companies.length}
                </span>
                left
              </div>
            </div>
          </div>

          <div className="flex-grow-1 overflow-auto p-3 small">
            {classifyRunning && (
              <div className="d-flex align-items-center gap-2 text-muted mb-2">
                <span className="spinner-border spinner-border-sm" role="status"></span>
                Finding each company on LinkedIn and placing it in a PINE industry…
              </div>
            )}
            {!classifyRunning && failed && (
              <p className="text-danger mb-2">Placing did not finish.</p>
            )}

            {notPlaced.length > 0 && (
              <div
                className="rounded px-2 py-2 mb-3"
                style={{ background: AMBER.tint, border: `1px solid ${AMBER.line}` }}
              >
                <div className="fw-semibold mb-1" style={{ color: AMBER.text }}>
                  Could not place ({notPlaced.length})
                </div>
                {notPlaced.map((k) => (
                  <div key={k.id} className="py-1">
                    {k.name}
                  </div>
                ))}
                <div className="text-muted fst-italic mt-1" style={LABEL_STYLE}>
                  No LinkedIn page found, or no PINE industry fits.
                </div>
              </div>
            )}

            {done.length > 0 && (
              <>
                <div className="text-muted text-uppercase fw-semibold mb-1" style={LABEL_STYLE}>
                  Placed in PINE
                </div>
                {done.map((k) => (
                  <div
                    key={k.id}
                    className="d-flex align-items-start justify-content-between gap-2 py-1"
                    style={{ borderBottom: "1px solid #F1F3F5" }}
                  >
                    <div style={{ minWidth: 0 }}>
                      {k.name}
                      {k.linkedin_industry && (
                        <div className="text-muted" style={LABEL_STYLE}>
                          LinkedIn: {k.linkedin_industry}
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 text-nowrap">
                      <span
                        className="rounded-pill px-2 fw-semibold"
                        style={{
                          ...LABEL_STYLE,
                          background: `${GREEN} / 0.14)`,
                          color: "#15803D",
                          border: `1px solid ${GREEN} / 0.3)`,
                        }}
                      >
                        {k.industry}
                      </span>
                      <button
                        type="button"
                        className="btn btn-light border btn-sm py-0 px-2"
                        style={LABEL_STYLE}
                        disabled={undoing.has(k.id)}
                        onClick={() => undoClassify(k)}
                        title="Put this company back to no industry, in PINE and on this map"
                      >
                        {undoing.has(k.id) ? "Undoing…" : "Undo"}
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {!classifyRunning && !notPlaced.length && !done.length && (
              <p className="text-muted fst-italic mb-0">
                {column.companies.length
                  ? "Waiting to be placed."
                  : "Every company on this map has an industry."}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const addSuggestion = async (s) => {
    setAdding((prev) => new Set(prev).add(s.id));
    try {
      const res = await axios.post(
        `${REACT_APP_API_BASE_URL}/workstation/gap_analysis/${id}/suggestion/${s.id}/add`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastr.success(
        res.data?.already_on_map
          ? `${s.name} is already on the map.`
          : `${s.name} added to ${s.industry}.`,
      );
      await Promise.all([loadMap(), loadAi()]);
    } catch (err) {
      toastr.error(err.response?.data?.error || "Could not add the company.");
    } finally {
      setAdding((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  };

  // "Add to Search Map" on a suggested industry: the column under its PINE
  // name, with its companies. Kept apart from company ids in `adding`.
  const addIndustry = async (s) => {
    const key = `industry-${s.id}`;
    setAdding((prev) => new Set(prev).add(key));
    try {
      const res = await axios.post(
        `${REACT_APP_API_BASE_URL}/workstation/gap_analysis/${id}/industry_suggestion/${s.id}/add`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const n = res.data?.added || 0;
      toastr.success(
        res.data?.already_added
          ? `${s.pine_industry} is already on the map.`
          : `${res.data?.industry || s.pine_industry} added to the Search Map with ${n} ${
              n === 1 ? "company" : "companies"
            }.`,
      );
      await Promise.all([loadMap(), loadAi()]);
    } catch (err) {
      toastr.error(err.response?.data?.error || "Could not add the industry.");
    } finally {
      setAdding((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const companies = columns.flatMap((c) => c.companies);
  const countOf = (status) =>
    companies.filter((c) => c.status === status).length;
  const mapped = countOf("mapped");
  const noPoach = countOf("no_poach");
  const notMapped = countOf("not_mapped");
  const share = (n) => (companies.length ? (n / companies.length) * 100 : 0);

  const tiles = [
    { label: "Industries in map", value: columns.length, cls: "" },
    { label: "Companies tracked", value: companies.length, cls: "" },
    { label: "Mapped", value: mapped, cls: "text-success" },
    { label: "No-poach", value: noPoach, cls: "text-danger" },
    { label: "Not yet mapped", value: notMapped, cls: "text-warning" },
  ];

  // Industry cards: most mapped first, then by name, Unclassified always last.
  // A map with no real industry gets a "Starting point" card for the role's
  // own industry, first.
  const mappedIn = (column) =>
    column.companies.filter((c) => c.status === "mapped").length;
  const ordered = [...columns].sort(
    (a, b) =>
      Number(isUnclassified(a.industry)) - Number(isUnclassified(b.industry)) ||
      mappedIn(b) - mappedIn(a) ||
      a.industry.localeCompare(b.industry),
  );
  const cards =
    !hasIndustry && startingIndustry
      ? [
          { id: "start", industry: startingIndustry, companies: [], start: true },
          ...ordered,
        ]
      : ordered;

  const suggestionsFor = (industry) =>
    (ai?.suggestions || []).filter((s) => sameIndustry(s.industry, industry));

  const lastRun = ai?.last_run || null;

  // The new-industries section: newest first, added ones stay.
  const industrySuggestions = ai?.industry_suggestions || [];
  const industriesLoading =
    !!running && (running.scope === "all" || running.scope === "new_industries");
  const industriesFailed =
    !running &&
    lastRun?.status === "failed" &&
    lastRun.scope === "new_industries";

  /** The AI part of one industry card. */
  const renderAiBox = (column) => {
    const list = suggestionsFor(column.industry);
    const loadingHere = covers(running, column.industry);
    // Only this card's own refresh is reported here. A whole-map run that
    // failed is said once, under the header, and the cards keep what they have.
    const failedHere =
      !running &&
      lastRun?.status === "failed" &&
      lastRun.scope === "industry" &&
      sameIndustry(lastRun.industry, column.industry);

    return (
      <>
        <div
          className="d-flex align-items-center justify-content-between text-primary text-uppercase fw-semibold mb-2"
          style={LABEL_STYLE}
        >
          <span>
            <i className="fa-solid fa-wand-magic-sparkles me-1"></i>
            AI suggested — not in the map
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            title={`Find more companies for ${column.industry}`}
            disabled={busy}
            onClick={() => startRun(column.industry)}
          >
            <i className="fa-solid fa-rotate-right"></i>
          </button>
        </div>

        {/* Unclassified has a card of its own; the AI never suggests for it. */}
        {!isUnclassified(column.industry) && (
          <>
            {loadingHere && (
              <div className="d-flex align-items-center gap-2 text-muted mb-2">
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                ></span>
                Finding companies…
              </div>
            )}

            {failedHere && (
              <div className="text-danger mb-2">
                Couldn't get suggestions.{" "}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  disabled={busy}
                  onClick={() =>
                    startRun(lastRun.scope === "all" ? null : column.industry)
                  }
                >
                  Try again
                </button>
              </div>
            )}

            {!loadingHere && !failedHere && list.length === 0 && (
              <p className="text-muted fst-italic mb-0">
                {ai ? "No suggestions yet." : "Loading…"}
              </p>
            )}

            <div className="d-flex flex-column gap-1">
              {list.map((s) => (
                <div
                  key={s.id}
                  className="d-flex align-items-start justify-content-between gap-2 rounded px-2 py-1"
                  style={{
                    border: `1px dashed ${ORANGE} / 0.3)`,
                    background: `${ORANGE} / 0.05)`,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-1">
                      <span className="fw-medium">{s.name}</span>
                      {/* With people in PINE it opens "Who do we already know there?". */}
                      <button
                        type="button"
                        className="rounded-pill px-2 text-nowrap border-0"
                        disabled={!(s.in_pine && s.pine_people > 0)}
                        title={
                          s.in_pine && s.pine_people > 0
                            ? `See who we know at ${s.name}`
                            : undefined
                        }
                        onClick={() =>
                          // Once added it is on the map: its own PINE links;
                          // until then, adding someone also adds it.
                          setWhoWeKnow(
                            s.added && s.map_company_id
                              ? { name: s.name, mapCompanyId: s.map_company_id }
                              : { name: s.name, suggestionId: s.id },
                          )
                        }
                        style={{
                          fontSize: 11,
                          ...(s.in_pine && s.pine_people > 0
                            ? { background: `${GREEN} / 0.15)`, color: "#15803D", cursor: "pointer" }
                            : { background: "#F1F3F5", color: "#6C757D" }),
                        }}
                      >
                        <i className="fa-solid fa-user me-1"></i>
                        {!s.in_pine
                          ? "Not in PINE yet"
                          : s.pine_people > 0
                            ? `PINE has ${s.pine_people} ${
                                s.pine_people === 1 ? "person" : "people"
                              } here`
                            : "In PINE, nobody there yet"}
                      </button>
                    </div>
                    {s.reason && (
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {s.reason}
                      </div>
                    )}
                  </div>
                  {s.added ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-light text-nowrap py-0"
                      disabled
                      title={
                        s.added_by
                          ? `Added by ${s.added_by}, ${when(s.added_at)}`
                          : undefined
                      }
                    >
                      <i className="fa-solid fa-check me-1 text-success"></i>
                      Added
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary text-nowrap py-0"
                      disabled={adding.has(s.id)}
                      onClick={() => addSuggestion(s)}
                    >
                      <i className="fa-solid fa-plus me-1"></i>
                      {adding.has(s.id) ? "Adding…" : "Add"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="content">
      {whoWeKnow && (
        <WhoWeKnow
          roleId={id}
          company={whoWeKnow}
          token={token}
          onClose={() => setWhoWeKnow(null)}
          onAdded={() => Promise.all([loadMap(), loadAi()])}
        />
      )}
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
            <div>
              <h4 className="mb-1">
                Gap Analysis{map ? ` — ${map.role.name}` : ""}
              </h4>
              {map && (
                <p className="text-muted mb-0">
                  {mapped} {mapped === 1 ? "company" : "companies"} mapped
                  across {columns.length}{" "}
                  {columns.length === 1 ? "industry" : "industries"}
                </p>
              )}
            </div>
            <div className="text-end">
              <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 small">
                <button
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                  onClick={openGrid}
                >
                  <i className="fa-solid fa-sitemap me-1"></i> Open in Search
                  Map
                </button>
                <button
                  className="btn btn-primary btn-sm d-inline-flex align-items-center"
                  onClick={() => startRun()}
                  disabled={!ai || busy || !canSuggest}
                >
                  {busy ? (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    ></span>
                  ) : (
                    <i className="fa-solid fa-wand-magic-sparkles me-1"></i>
                  )}
                  {busy ? "Analysing…" : "Refresh AI suggestions"}
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
                  onClick={handleBack}
                >
                  <i className="ti ti-arrow-left me-1"></i> Back to workstation
                </button>
              </div>
              {!running &&
                lastRun?.status === "failed" &&
                lastRun.scope === "all" && (
                  <div className="text-danger small mt-1">
                    The last refresh didn't finish.{" "}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      disabled={busy || !canSuggest}
                      onClick={() => startRun()}
                    >
                      Try again
                    </button>
                  </div>
                )}
            </div>
          </div>

          {loading && !map && (
            <p className="text-muted mb-0">Loading the search map…</p>
          )}

          {!loading && error && (
            <div className="alert alert-danger mb-0">{error}</div>
          )}

          {aiError && (
            <div className="alert alert-warning py-2 small">{aiError}</div>
          )}

          {map && !error && (
            <>
              <div className="row g-3">
                {tiles.map((t) => (
                  <div key={t.label} className="col-6 col-md">
                    <div className="border rounded p-3 h-100">
                      <div className={`fs-3 fw-bold ${t.cls}`}>{t.value}</div>
                      <div className="text-muted small mt-1">{t.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border rounded p-3 mt-3">
                <div className="d-flex align-items-center justify-content-between small mb-2">
                  <span className="fw-semibold">Map coverage</span>
                  <span className="text-muted">
                    {Math.round(share(mapped))}% of tracked companies mapped
                  </span>
                </div>
                <div
                  className="d-flex rounded-pill overflow-hidden"
                  style={{ height: 8, background: "#e9ecef" }}
                >
                  <div
                    style={{
                      width: `${share(mapped)}%`,
                      background: `${GREEN})`,
                    }}
                  />
                  <div
                    style={{
                      width: `${share(noPoach)}%`,
                      background: `${RED} / 0.8)`,
                    }}
                  />
                </div>
              </div>

              {columns.length === 0 && (
                <div className="text-center border rounded py-5 px-3 mt-3">
                  <i className="fa-solid fa-sitemap fs-1 text-muted mb-3 d-block"></i>
                  <h5 className="mb-1">This map is empty</h5>
                  <p className="text-muted mb-3">
                    {startingIndustry
                      ? `Add companies in the Search Map, or start from the AI's suggestions for ${startingIndustry} below.`
                      : "Add an industry to the Search Map and AI will suggest companies for it."}
                  </p>
                  <button
                    className="btn btn-primary btn-sm d-inline-flex align-items-center"
                    onClick={openGrid}
                  >
                    <i className="fa-solid fa-sitemap me-1"></i> Open in Search
                    Map
                  </button>
                </div>
              )}

              {columns.length > 0 && !hasIndustry && !startingIndustry && ai && (
                <div className="alert alert-light border mt-3 mb-0 small">
                  Add an industry to the Search Map and AI will suggest
                  companies for it.
                </div>
              )}

              {cards.length > 0 && (
                <>
                  <div className="d-flex align-items-baseline gap-2 mt-4 mb-3">
                    <i className="fa-solid fa-building text-primary"></i>
                    <h5 className="mb-0">Industries under search</h5>
                    <span className="text-muted small">
                      ({columns.length}) — read live from the search map
                    </span>
                  </div>
                  <div className="row g-3">
                    {cards.map((column) => {
                      if (isUnclassified(column.industry)) {
                        return renderUnclassifiedCard(column);
                      }
                      const total = column.companies.length;
                      const done = column.companies.filter(
                        (c) => c.status === "mapped",
                      );
                      const off = column.companies.filter(
                        (c) => c.status === "no_poach",
                      );
                      const todo = column.companies.filter(
                        (c) => c.status === "not_mapped",
                      );
                      const part = (n) => (total ? (n / total) * 100 : 0);

                      return (
                        <div key={column.id} className="col-md-6 col-lg-4">
                          <div
                            className="border rounded d-flex flex-column"
                            style={{
                              height: 288,
                              ...(column.start && {
                                borderStyle: "dashed",
                              }),
                            }}
                          >
                            <div className="p-3 border-bottom">
                              <div className="d-flex align-items-start justify-content-between gap-2">
                                <div>
                                  <h6 className="fw-semibold mb-0">
                                    {column.start
                                      ? `Starting point — ${column.industry}`
                                      : column.industry}
                                  </h6>
                                  <div className="text-muted small">
                                    {column.start
                                      ? "The role's own industry. Add creates it on the map."
                                      : column.owner?.name
                                        ? `Owner: ${column.owner.name}`
                                        : "Owner unassigned"}
                                  </div>
                                </div>
                                {!column.start && (
                                  <span className="small fw-semibold text-success text-nowrap">
                                    {done.length}/{total} mapped
                                  </span>
                                )}
                              </div>
                              {!column.start && (
                                <div
                                  className="d-flex rounded-pill overflow-hidden mt-2"
                                  style={{ height: 6, background: "#e9ecef" }}
                                >
                                  <div
                                    style={{
                                      width: `${part(done.length)}%`,
                                      background: `${GREEN})`,
                                    }}
                                  />
                                  <div
                                    style={{
                                      width: `${part(off.length)}%`,
                                      background: `${RED} / 0.8)`,
                                    }}
                                  />
                                </div>
                              )}
                            </div>

                            <div className="flex-grow-1 overflow-auto p-3 small">
                              {!column.start && (
                                <>
                                  <div className="text-muted text-uppercase fw-semibold mb-2" style={LABEL_STYLE}>
                                    Mapped
                                  </div>
                                  {done.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                      {done.map((c) => (
                                        <span
                                          key={c.id}
                                          className="rounded-pill px-2 py-1"
                                          style={{
                                            background: `${GREEN} / 0.15)`,
                                            border: `1px solid ${GREEN} / 0.3)`,
                                          }}
                                        >
                                          <i className="fa-solid fa-building me-1 text-success"></i>
                                          {c.name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-muted mb-3">
                                      Nothing mapped yet — {total}{" "}
                                      {total === 1 ? "company" : "companies"} in
                                      the grid.
                                    </p>
                                  )}

                                  {off.length > 0 && (
                                    <>
                                      <div className="text-muted text-uppercase fw-semibold mb-2" style={LABEL_STYLE}>
                                        No-poach
                                      </div>
                                      <div className="d-flex flex-wrap gap-1 mb-3">
                                        {off.map((c) => (
                                          <span
                                            key={c.id}
                                            className="border rounded-pill px-2 py-1 text-muted text-decoration-line-through"
                                          >
                                            <i className="fa-solid fa-ban me-1 text-danger"></i>
                                            {c.name}
                                          </span>
                                        ))}
                                      </div>
                                    </>
                                  )}

                                  {todo.length > 0 && (
                                    <>
                                      <div className="text-muted text-uppercase fw-semibold mb-2" style={LABEL_STYLE}>
                                        In the grid, not yet mapped
                                      </div>
                                      <p className="text-muted mb-3">
                                        {/* A company linked to PINE opens "Who do we
                                            already know there?". */}
                                        {todo.map((c, i) => (
                                          <React.Fragment key={c.id}>
                                            {i > 0 && ", "}
                                            {c.linked ? (
                                              <button
                                                type="button"
                                                className="btn btn-link p-0 align-baseline text-muted"
                                                style={{ fontSize: "inherit" }}
                                                title={`See who we know at ${c.name}`}
                                                onClick={() =>
                                                  setWhoWeKnow({
                                                    name: c.name,
                                                    mapCompanyId: c.id,
                                                  })
                                                }
                                              >
                                                {c.name}
                                              </button>
                                            ) : (
                                              c.name
                                            )}
                                          </React.Fragment>
                                        ))}
                                      </p>
                                    </>
                                  )}
                                </>
                              )}

                              {renderAiBox(column)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-4 mb-1">
                <div className="d-flex align-items-baseline gap-2">
                  <i className="fa-solid fa-wand-magic-sparkles text-primary"></i>
                  <h5 className="mb-0">AI suggested new industries</h5>
                  {industrySuggestions.length > 0 && (
                    <span className="text-muted small">
                      ({industrySuggestions.length})
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                  disabled={!ai || busy}
                  onClick={() => startRun(null, true)}
                >
                  {industriesLoading ? (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    ></span>
                  ) : (
                    <i className="fa-solid fa-magnifying-glass-plus me-1"></i>
                  )}
                  {industriesLoading ? "Finding industries…" : "Find new industries"}
                </button>
              </div>
              <p className="text-muted small mb-2">
                Industries this map does not cover yet. Adding one creates it in
                the Search Map with its companies.
              </p>

              {industriesFailed && (
                <div className="text-danger small mb-2">
                  Couldn't get new industries.{" "}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 align-baseline"
                    disabled={busy}
                    onClick={() => startRun(null, true)}
                  >
                    Try again
                  </button>
                </div>
              )}

              {industriesLoading && (
                <div className="d-flex align-items-center gap-2 text-muted small mb-2">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  Finding industries this map does not cover…
                </div>
              )}

              {industrySuggestions.length === 0 && !industriesLoading && (
                <div
                  className="rounded p-3 text-muted fst-italic small"
                  style={{ border: "1px dashed #dee2e6" }}
                >
                  No new industries suggested yet.
                </div>
              )}

              {industrySuggestions.length > 0 && (
                <div className="d-flex gap-3 overflow-auto pb-2">
                  {industrySuggestions.map((s) => (
                    <div
                      key={s.id}
                      className="rounded p-3 d-flex flex-column small"
                      style={{
                        flex: "0 0 320px",
                        border: `1px dashed ${ORANGE} / 0.4)`,
                        background: `${ORANGE} / 0.05)`,
                      }}
                    >
                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                        {s.name}
                      </div>
                      <div className="mt-1">
                        <span
                          className="rounded-pill px-2 text-nowrap"
                          style={{
                            fontSize: 11,
                            ...(s.pine_industry
                              ? { background: `${ORANGE} / 0.15)`, color: "#9A3412" }
                              : { background: "#F1F3F5", color: "#6C757D" }),
                          }}
                        >
                          PINE: {s.pine_industry || "Unclassified"}
                        </span>
                      </div>
                      {s.reason && (
                        <p className="text-muted mt-2 mb-2" style={{ fontSize: 12 }}>
                          {s.reason}
                        </p>
                      )}
                      <div className="d-flex flex-wrap gap-1 mb-3">
                        {s.companies.map((c) => (
                          <button
                            type="button"
                            key={c.name}
                            className="rounded-pill px-2 py-1 bg-white border"
                            disabled={!(c.in_pine && c.pine_people > 0)}
                            onClick={() =>
                              // Until the industry is added, adding someone
                              // from it adds the whole industry too.
                              setWhoWeKnow(
                                s.added
                                  ? { name: c.name }
                                  : {
                                      name: c.name,
                                      industrySuggestionId: s.id,
                                      industryName: s.name,
                                      pineIndustry: s.pine_industry,
                                    },
                              )
                            }
                            style={{
                              fontSize: 11,
                              color: "inherit",
                              cursor: c.in_pine && c.pine_people > 0 ? "pointer" : "default",
                            }}
                            title={
                              !c.in_pine
                                ? "Not in PINE yet"
                                : c.pine_people > 0
                                  ? `PINE has ${c.pine_people} ${
                                      c.pine_people === 1 ? "person" : "people"
                                    } here - see who fits`
                                  : "In PINE, nobody there yet"
                            }
                          >
                            {c.name}
                            {c.in_pine && c.pine_people > 0 && (
                              <span className="text-success ms-1">
                                <i className="fa-solid fa-user ms-1 me-1"></i>
                                {c.pine_people}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="mt-auto">
                        {s.added ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-light w-100"
                            disabled
                          >
                            <i className="fa-solid fa-check me-1 text-success"></i>
                            Added to map
                          </button>
                        ) : !s.pine_industry ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary w-100"
                              disabled
                            >
                              <i className="fa-solid fa-plus me-1"></i>
                              Add to Search Map
                            </button>
                            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                              No PINE industry fits this yet.
                            </div>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary w-100"
                            disabled={adding.has(`industry-${s.id}`)}
                            onClick={() => addIndustry(s)}
                          >
                            <i className="fa-solid fa-plus me-1"></i>
                            {adding.has(`industry-${s.id}`)
                              ? "Adding…"
                              : "Add to Search Map"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-muted small mt-4 mb-0">
                Coverage figures are read live from the search map — anything
                added here shows up in the grid. A company counts as mapped
                only once candidates are recorded against it, so flagged
                companies without candidates are still treated as gaps.
                Suggested companies come from AI on the date shown; check them
                before relying on them.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
